import { Box, Button, Stack, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { branding } from "../../data/api";
import { useAction, useLoad } from "../../data/data";
import { StatusBadge } from "../../ui/components/StatusBadge";
import {
  Failure,
  Field,
  FormRow,
  Hint,
  Loading,
  PageHeader,
  Section,
} from "../../ui/components/primitives";
import { useTokens } from "../../ui/theme/theme";
import { Accented, LandingBox, type PageBranding } from "../authn/LandingLayout";
import type { TenantPageProps } from "../pages";

// ---
// The pages people reach from a link in their mail
//
// Setting a password after an invitation, and choosing a new one after a reset. They are the only
// part of this console somebody who is not an administrator ever sees, and until now they said
// FlexAuth to a person who has only ever heard of the tenant.
//
// Per tenant, not per application, for the same reason the email templates are: the application is
// a value these pages display, not a scope they are keyed by. A tenant that needs two genuinely
// different looks hosts its own pages against the public endpoints instead.
// ---

/** Empty means "use what the server ships", which is a different thing from an empty string. */
type Draft = { displayName: string; logoUrl: string; accent: string; supportEmail: string };

const asNull = (value: string): string | null => (value.trim() === "" ? null : value.trim());

/** The draft as the pages themselves take it, so the preview is fed what the server would send. */
const asBranding = (draft: Draft): PageBranding => ({
  displayName: asNull(draft.displayName),
  logoUrl: asNull(draft.logoUrl),
  accent: asNull(draft.accent),
  supportEmail: asNull(draft.supportEmail),
});

/**
 * The two pages a link in the mail can lead to.
 *
 * Their wording is the wording in `Accept.tsx` and `Reset.tsx`. The chrome around them is not a
 * copy — it is `LandingBox`, the component those pages use.
 */
const PREVIEWS = [
  {
    label: "From an invitation",
    title: "Set your password",
    subtitle: (name: string) => (
      <>someone@example.com joining {name}. The invitation expires in 7 days.</>
    ),
    fields: ["New password", "Confirm"],
    action: "Set password",
  },
  {
    label: "From a password reset",
    title: "Choose a new password",
    subtitle: () => <>This link works once, and expires an hour after it was sent.</>,
    fields: ["New password", "Confirm"],
    action: "Set password",
  },
] as const;

export function LandingPages({ tenant, token, scopes }: TenantPageProps): React.JSX.Element {
  const tokens = useTokens();
  const current = useLoad(() => branding.get(tenant, token), [tenant, token]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const save = useAction(() => current.reload());
  const revert = useAction(() => current.reload());
  const canManage = scopes.includes("user-flow:manage");

  const loaded = current.data ?? null;
  useEffect(() => {
    if (loaded === null) return;
    setDraft({
      displayName: loaded.displayName ?? "",
      logoUrl: loaded.logoUrl ?? "",
      accent: loaded.accent ?? "",
      supportEmail: loaded.supportEmail ?? "",
    });
  }, [loaded]);

  if (current.pending) return <Loading what="branding" />;
  if (draft === null || loaded === null) {
    return (
      <>
        <PageHeader title="Landing pages" />
        <Failure error={current.error} />
      </>
    );
  }

  const name = draft.displayName.trim() === "" ? tenant : draft.displayName.trim();
  const accent = /^#[0-9a-fA-F]{6}$/.test(draft.accent) ? draft.accent : tokens.accent.base;
  const accentWrong = draft.accent !== "" && !/^#[0-9a-fA-F]{6}$/.test(draft.accent);

  return (
    <>
      <PageHeader title="Landing pages" />

      <Section
        title="Appearance"
        action={
          <StatusBadge
            label={loaded.customised ? "Customised" : "Default"}
            tone={loaded.customised ? "success" : "neutral"}
          />
        }
      >
        <Hint sx={{ mt: 0, mb: 4 }}>
          Shown when somebody opens an invitation or a password-reset link. These are the only pages
          here that a person who is not an administrator ever sees.
        </Hint>

        <FormRow>
          <Field label="Name shown to people" sx={{ flex: "1 1 260px", maxWidth: 360 }}>
            <TextField
              size="small"
              value={draft.displayName}
              disabled={!canManage}
              placeholder={tenant}
              onChange={(event) => setDraft({ ...draft, displayName: event.target.value })}
            />
          </Field>
          <Field label="Accent" sx={{ flex: "0 1 160px" }} error={accentWrong}>
            <TextField
              size="small"
              value={draft.accent}
              disabled={!canManage}
              placeholder="#1f6feb"
              error={accentWrong}
              onChange={(event) => setDraft({ ...draft, accent: event.target.value })}
            />
          </Field>
        </FormRow>

        <FormRow sx={{ mt: 3 }}>
          <Field label="Logo URL" sx={{ flex: "1 1 320px", maxWidth: 440 }}>
            <TextField
              size="small"
              value={draft.logoUrl}
              disabled={!canManage}
              placeholder="https://example.com/logo.svg"
              onChange={(event) => setDraft({ ...draft, logoUrl: event.target.value })}
            />
          </Field>
          <Field label="Support address" sx={{ flex: "1 1 240px", maxWidth: 320 }}>
            <TextField
              size="small"
              value={draft.supportEmail}
              disabled={!canManage}
              placeholder="support@example.com"
              onChange={(event) => setDraft({ ...draft, supportEmail: event.target.value })}
            />
          </Field>
        </FormRow>
        <Hint>
          The logo must be served over https, from somewhere that allows it to be embedded. Leave
          anything blank to use what FlexAuth ships.
        </Hint>

        <Failure error={save.error ?? revert.error} />

        {canManage && (
          <FormRow sx={{ mt: 5, justifyContent: "flex-end" }}>
            {loaded.customised && (
              <Button
                variant="outlined"
                disabled={revert.busy}
                onClick={() => revert.run(() => branding.revert(tenant, token))}
              >
                Revert to default
              </Button>
            )}
            <Button
              variant="contained"
              disabled={save.busy || accentWrong}
              onClick={() =>
                save.run(() =>
                  branding.save(tenant, token, {
                    displayName: asNull(draft.displayName),
                    logoUrl: asNull(draft.logoUrl),
                    accent: asNull(draft.accent),
                    supportEmail: asNull(draft.supportEmail),
                  }),
                )
              }
            >
              Save
            </Button>
          </FormRow>
        )}
      </Section>

      <Section title="Preview">
        <Hint sx={{ mt: 0, mb: 4 }}>
          Both pages, drawn by the component that serves them — so what is here is what arrives. One
          box on a background and nothing beside it: no panel describing FlexAuth to somebody who
          has never heard of us. The form is live; nothing it does is saved.
        </Hint>
        <Box
          sx={{
            display: "grid",
            gap: 6,
            gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
            bgcolor: tokens.surface.bg,
            borderRadius: `${tokens.radius.xl}px`,
            p: { xs: 4, md: 6 },
          }}
        >
          {PREVIEWS.map((preview) => (
            <Box key={preview.label}>
              <Box
                sx={{
                  typography: "overline",
                  color: tokens.ink.muted,
                  mb: 3,
                  textAlign: "center",
                }}
              >
                {preview.label}
              </Box>
              <Box sx={{ display: "grid", placeItems: "center" }}>
                <Accented accent={accent}>
                  <LandingBox
                    brand={asBranding(draft)}
                    title={preview.title}
                    subtitle={preview.subtitle(name)}
                  >
                    <Stack spacing={4}>
                      {preview.fields.map((label) => (
                        <Field key={label} label={label} hint=" ">
                          <TextField type="password" fullWidth size="small" />
                        </Field>
                      ))}
                      <Button variant="contained" size="large" fullWidth>
                        {preview.action}
                      </Button>
                    </Stack>
                  </LandingBox>
                </Accented>
              </Box>
            </Box>
          ))}
        </Box>
      </Section>
    </>
  );
}
